import type { ResolveResponse } from "../../shared/api.ts";

/**
 * Fetching and decoding a Clip.
 *
 * The audio is fetched and decoded ONCE per Round, never per Step. Two reasons:
 * a decoded AudioBuffer makes the signed url irrelevant the moment it lands
 * (ADR 0001 — the 15-minute expiry then cannot interrupt a Round), and
 * sample-accurate playback needs the decoded samples anyway (ADR 0002).
 *
 * The 30s buffer means the client holds the whole song from Step 1. That leak
 * is accepted deliberately — see ADR 0004.
 */

export interface LoadClipDeps {
  fetchImpl?: typeof fetch;
  decode: (bytes: ArrayBuffer) => Promise<AudioBuffer>;
}

export class ClipUnavailableError extends Error {
  constructor(
    message: string,
    readonly kind: "quota" | "upstream" | "not-found" | "unknown",
  ) {
    super(message);
    this.name = "ClipUnavailableError";
  }
}

async function resolve(trackId: number, fetchImpl: typeof fetch): Promise<ResolveResponse> {
  const res = await fetchImpl(`/api/resolve/${trackId}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { kind?: string; error?: string } | null;
    throw new ClipUnavailableError(
      body?.error ?? `resolve failed (${res.status})`,
      (body?.kind as ClipUnavailableError["kind"]) ?? "unknown",
    );
  }
  return (await res.json()) as ResolveResponse;
}

/**
 * Loads a Track's audio, re-resolving once if the signed url has expired.
 *
 * The retry is what makes the expiry invisible to players: a tab left open
 * overnight and resumed cold gets a 403, silently re-resolves, and plays. No
 * countdown is ever shown — that would manufacture worry about a non-issue.
 */
export async function loadClip(
  trackId: number,
  deps: LoadClipDeps,
): Promise<{ buffer: AudioBuffer; expiresAt: number | null }> {
  const fetchImpl = deps.fetchImpl ?? fetch;

  let resolved = await resolve(trackId, fetchImpl);
  let audio = await fetchImpl(resolved.audioUrl);

  if (audio.status === 403) {
    resolved = await resolve(trackId, fetchImpl);
    audio = await fetchImpl(resolved.audioUrl);
  }

  if (!audio.ok) {
    throw new ClipUnavailableError(`audio fetch failed (${audio.status})`, "upstream");
  }

  const buffer = await deps.decode(await audio.arrayBuffer());
  return { buffer, expiresAt: resolved.expiresAt };
}
